import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';
import { useMemo } from 'react';
import { offlineAction } from '../functions/offline.helper';
import { toaster } from '../snackbar';
export const useUser = (api, shareId, managerInviteCode) => {
    const userQueryKey = useMemo(() => ['user'], []);
    const queryClient = useQueryClient();
    const userRefreshMutation = useMutation({
        networkMode: 'always',
        mutationFn: async () => api.user.refresh(),
        onMutate: () => {
            queryClient.cancelQueries({ queryKey: userQueryKey });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(userQueryKey);
            localStorage.clear();
        },
    });
    const userRefresh = () => {
        const mutation = (func) => {
            try {
                userRefreshMutation.mutate(undefined, {
                    onSuccess: () => {
                        func();
                    },
                });
            }
            catch (e) {
                offlineAction(e);
            }
        };
        return {
            mutation,
            isLoading: userRefreshMutation.isLoading,
        };
    };
    const userDeleteMutation = useMutation({
        networkMode: 'always',
        mutationFn: async () => api.user.deleteUser(),
        onMutate: () => {
            queryClient.cancelQueries({ queryKey: userQueryKey });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(userQueryKey);
            localStorage.clear();
        },
    });
    const userDelete = () => {
        const mutationAsync = async (func) => {
            try {
                await userDeleteMutation.mutateAsync(undefined, {
                    onSuccess: () => {
                        func();
                    },
                });
            }
            catch (e) {
                offlineAction(e);
            }
        };
        return {
            mutationAsync,
            isLoading: userDeleteMutation.isLoading,
        };
    };
    const switchModeMutation = useMutation({
        networkMode: 'always',
        mutationFn: async (mode) => api.user.switchMode(mode),
        onMutate: () => {
            queryClient.cancelQueries({ queryKey: userQueryKey });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(userQueryKey);
        },
    });
    const switchMode = () => {
        const mutation = (portalMode, func) => {
            try {
                switchModeMutation.mutate(portalMode, {
                    onSuccess: () => {
                        func();
                    },
                });
            }
            catch (e) {
                offlineAction(e);
            }
        };
        return {
            mutation,
            isLoading: switchModeMutation.isLoading,
        };
    };
    const user = useQuery({
        queryKey: [...userQueryKey, shareId, managerInviteCode],
        queryFn: async () => {
            const { data } = await api.user.getUser(shareId, managerInviteCode);
            return data;
        },
        staleTime: minutesToMilliseconds(30),
    });
    const isManager = useQuery({
        queryKey: [...userQueryKey, 'isManager'],
        queryFn: async () => {
            const { data } = await api.user.isManager();
            return data;
        },
        staleTime: minutesToMilliseconds(240),
    });
    const managerDetails = useQuery({
        queryKey: ['user', 'managerDetails'],
        queryFn: async () => {
            if (!managerInviteCode)
                return null;
            const { data } = await api.user.managerDetails(managerInviteCode);
            return data;
        },
        staleTime: minutesToMilliseconds(240),
    });
    return {
        userRefresh,
        userDelete,
        user,
        switchMode,
        isManager,
        managerDetails,
    };
};
export const useManager = (api) => {
    const mode = useQuery({
        queryKey: ['user', 'mode'],
        queryFn: async () => {
            const modeData = await api.user.getMode();
            if (modeData?.data) {
                return modeData?.data;
            }
        },
    });
    return {
        mode,
    };
};
export const useRemoveEmployee = (api, getTeamSummaryQueryKey, onMutate) => {
    const queryClient = useQueryClient();
    const removeEmployee = useMutation({
        mutationFn: async (employeeUserId) => await api.user.removeEmployee(employeeUserId),
        onMutate: onMutate,
        onError: (e) => {
            const err = e;
            if (err?.message) {
                toaster.error(err.message);
            }
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getTeamSummaryQueryKey }),
    });
    return removeEmployee;
};
export const useDirectReports = (api) => {
    const directReports = useQuery({
        queryKey: ['user', 'directReports'],
        queryFn: async () => {
            const directReports = await api.user.getDirectReports();
            if (directReports?.data) {
                return directReports?.data;
            }
        },
    });
    return {
        directReports,
    };
};
